import constructDialog from '../../utils/webcomponents/construct-dialog';
import { getTranslation } from '../../utils/webcomponents/translate';

import { get, getProductDetails } from './turbomap-api';
import events from './turbomap-events';

import TurbomapHeader from './turbomap-header';
import TurbomapVisual from './turbomap-visual';

class Turbomap {
	constructor(el) {
		this.el = el;

		this.hasLoaded = false;
		this.isLoading = false;
		this.isError = false;
		this.hasBarrier = false;

		this.productCount = 0;

		this.message = this.el.querySelector('x-message');
		this.header = new TurbomapHeader(this.el.querySelector('.js-turbomap-header'));
		this.visual = new TurbomapVisual(this.el.querySelector('.js-turbomap-visual'));
		this.zoomout = this.el.querySelector('.js-turbomap-zoomout');

		this.zoomout.addEventListener('click', e => this.onZoomOutClicked(e));
		this.el.addEventListener(events.PRODUCT_CLICKED, e => this.onProductClicked(e));
		this.el.addEventListener(events.GROUP_CLICKED, e => this.onGroupClicked(e));
	}

	get isLoading() {
		return this.el.classList.contains('is-loading');
	}

	set isLoading(loading) {
		this.el.classList.toggle('is-loading', loading);
	}

	get isEmpty() {
		return this.hasLoaded &&
			!this.isError &&
			!this.isLoading &&
			this.productCount === 0;
	}

	get hasNoBarrier() {
		return this.hasLoaded &&
			!this.isError &&
			!this.hasBarrier;
	}

	get showMessage() {
		return this.isEmpty || this.isError || this.hasNoBarrier;
	}

	async load(filters = {}) {
		this.isLoading = true;
		this.isError = false;
		this.hasBarrier = false;

		let data = {};

		try {
			data = await get(filters);
			this.dataHistory = [];
			this.currentData = data;
		} catch (e) {
			this.isError = true;

			throw Error(e);
		} finally {
			this.isLoading = false;
			this.hasLoaded = true;

			this.update(data);
			this.updateMessage();
		}
	}

	async loadProductDetails({ isin }) {
		this.isLoading = true;

		try {
			const productDetails = await getProductDetails(isin);

			const dialogOptions = {
				title: productDetails.name,
			};

			const productDetailOptions = {
				details: JSON.stringify(productDetails),
			};

			constructDialog(dialogOptions, 'x-turbomap-product-detail', productDetailOptions);
		} catch (e) {
			throw Error('Error in getting product details', e);
		} finally {
			this.isLoading = false;
		}
	}


	update(data, isZoom = false) {
		const { underlying, products = [], barrierBaseLine } = data;

		if (!isZoom) {
			this.dataHistory = [];
			this.currentData = data;
			this.zoomout.classList.toggle('is-hidden', true);
		}

		this.productCount = products.length;
		this.hasBarrier = barrierBaseLine !== null;

		this.header.update(underlying);
		this.visual.update(data, isZoom);
	}

	async updateMessage() {
		const labelKey = this.getMessagLabelKey();
		const message = await getTranslation('turbomap', labelKey, labelKey);

		const { showMessage } = this;

		this.message.classList.toggle('is-hidden', !showMessage);

		const icon = this.isError ? 'warning' : '';
		const modifier = this.isError ? 'error' : 'empty';

		this.message.setAttribute('message', message);
		this.message.setAttribute('icon', icon);
		this.message.setAttribute('modifier', modifier);
	}

	getMessagLabelKey() {
		if (this.hasNoBarrier) {
			return 'noUnderlyingQuote';
		}

		if (this.isError) {
			return 'error';
		}

		return 'noProducts';
	}

	onProductClicked({ detail: product }) {
		this.loadProductDetails(product);
	}

	onGroupClicked({ detail: group }) {
		const barriers = group.subProducts.map(x => x.barrier);
		const newData = {
			barrierMin: Math.min(...barriers),
			barrierMax: Math.max(...barriers),
			barrierBaseLine: this.currentData.barrierBaseLine,
			dates: [group.subProducts[0].date],
			products: group.subProducts,
			underlying: this.currentData.underlying,
		};

		this.dataHistory.push(this.currentData);
		this.currentData = newData;

		this.update(newData, true);
		this.updateMessage();

		this.zoomout.classList.toggle('is-hidden', false);
	}

	onZoomOutClicked() {
		const isGroundLevel = this.dataHistory.length === 1;

		this.currentData = this.dataHistory.pop();
		this.update(this.currentData, !isGroundLevel);
		this.updateMessage();
		this.zoomout.classList.toggle('is-hidden', isGroundLevel);
	}
}

export default Turbomap;
